// frontend/src/features/inventory/lookup/lotScan.ts  (فایل جدید)
// AM-036 فاز ۲: منطق خالص (بدون React) تبدیل نتیجهٔ lookup به مقادیر فرم.

import type { LotLookupResponse } from "./lotLookupTypes";

export type StockFormTxType =
  | "receipt"
  | "issue"
  | "transfer_in"
  | "transfer_out"
  | "opening_balance";

export interface LotFormPatch {
  materialId: string;
  lotId: string;
  /** اگر پالت فقط در یک انبار موجودی دارد همان انبار؛ در غیر این صورت رشتهٔ خالی. */
  warehouseId: string;
  /** سقف مقدار خروج در انبار تعیین‌شده؛ اگر انبار هنوز انتخاب نشده null. */
  maxQuantity: string | null;
}

export type LotScanOutcome =
  | { ok: true; patch: LotFormPatch }
  | { ok: false; message: string };

/** نوع‌هایی که اسکن پالت برایشان معتبر است (AM-036 §2.3). */
const SCAN_ALLOWED: ReadonlySet<StockFormTxType> = new Set(["issue", "transfer_out"]);

export function applyLotToForm(lot: LotLookupResponse, txType: StockFormTxType): LotScanOutcome {
  if (!SCAN_ALLOWED.has(txType)) {
    return { ok: false, message: "اسکن پالت فقط برای خروج/انتقال معتبر است." };
  }
  if (txType === "issue" && !lot.is_releasable) {
    const label = lot.qc_status_label ?? lot.qc_status ?? "نامشخص";
    return {
      ok: false,
      message: `این پالت وضعیت کیفیِ «${label}» دارد و قابل خروج نیست. برای انتقال به انبار قرنطینه از فرم انتقال استفاده کنید.`,
    };
  }
  if (lot.stock.length === 0) {
    return { ok: false, message: `پالت ${lot.lot_number} در هیچ انباری موجودی ندارد.` };
  }
  const base = { materialId: lot.material_family_id, lotId: lot.id };
  if (lot.stock.length === 1) {
    const only = lot.stock[0]!;
    return {
      ok: true,
      patch: { ...base, warehouseId: only.warehouse_id, maxQuantity: only.remaining_qty },
    };
  }
  return { ok: true, patch: { ...base, warehouseId: "", maxQuantity: null } };
}

/** موجودی پالت در انبار انتخاب‌شده؛ null یعنی در آن انبار موجودی ندارد. */
export function remainingInWarehouse(lot: LotLookupResponse, warehouseId: string): string | null {
  const entry = lot.stock.find((s) => s.warehouse_id === warehouseId);
  return entry ? entry.remaining_qty : null;
}

/** پیام خطا برای انتخاب انباری که پالت در آن موجودی ندارد؛ null یعنی مشکلی نیست. */
export function warehouseMismatchMessage(
  lot: LotLookupResponse,
  warehouseId: string,
): string | null {
  if (!warehouseId) return null;
  return remainingInWarehouse(lot, warehouseId) === null
    ? "این پالت در انبار انتخاب‌شده موجودی ندارد."
    : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * استخراج پیام قابل‌خواندن از بدنهٔ خطای DRF (detail، خطاهای فیلدی، رشته).
 * جایگزین JSON.stringify خام در نمایش خطا.
 */
export function extractApiErrorMessage(data: unknown, fallback: string): string {
  if (typeof data === "string" && data.trim()) return data;
  if (Array.isArray(data)) {
    const parts = data.filter((x): x is string => typeof x === "string");
    return parts.length ? parts.join(" ") : fallback;
  }
  if (!isRecord(data)) return fallback;
  if (typeof data.detail === "string" && data.detail.trim()) return data.detail;
  const messages: string[] = [];
  for (const value of Object.values(data)) {
    if (typeof value === "string") messages.push(value);
    else if (Array.isArray(value)) {
      messages.push(...value.filter((x): x is string => typeof x === "string"));
    }
  }
  return messages.length ? messages.join(" ") : fallback;
}
